import React, { useState } from 'react';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { CategoryResult } from '../../../api/useResultsQuery';
import { Badge } from '../../../../CommonComponents/OPL/Badge/Badge';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Slider } from '../../../../CommonComponents/OPL/Slider/Slider';
import { getFulfillmentLabel } from '../../../Workflow/components/Fulfillment/Fulfillment.meta';
import { COLORS } from '../../../PulseCheck.meta';
import { CategoryGoals } from './components/CategoryGoals/CategoryGoals';
import { useSelector } from 'react-redux';
import { selectDisplayBehavioralFinanceSection } from '../../../../CommonSelectors/CommonAreaSelector';

interface Props {
  clientId: string;
  canAddGoals: boolean;
  category: CategoryResult;
}

export const Category = ({ clientId, canAddGoals, category }: Props) => {
  const [isManagingGoals, setIsManagingGoals] = useState(false);

  const DisplayBehavioralFinanceSection = useSelector(selectDisplayBehavioralFinanceSection);

  const isFinancialPlanEnabled = !DisplayBehavioralFinanceSection;

  const isTopCategory = category.priority < 4;

  const onSetGoal = (categoryId: string) => {
    ADV.util.Router.go2('PulseCheckSetGoal', { clientId, categoryId });
  };

  return (
    <Box display="flex" borderColor="neutral.200" borderWidth="0px 0px 1px 0px" borderStyle="solid">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderColor="neutral.200"
        borderWidth="0px 1px 0px 0px"
        borderStyle="solid"
        flexBasis={56}
      >
        <Text fontWeight={600} fontSize="h5">{`#${category.priority}`}</Text>
      </Box>

      <Box display="flex" flexDirection="column" flex={1} css="gap: 16px" p={16}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" flex={1}>
            <Box width={24} height={24} style={{ color: COLORS.primary }} mr="8">
              <category.icon />
            </Box>

            <Box display="flex" flexDirection="column">
              <Text fontWeight={600} fontSize="h6">
                {category.id}
              </Text>
              <Text color="neutral.500">{category.description}</Text>
            </Box>
          </Box>

          {isTopCategory && (
            <Badge
              variant={category.priority === 1 ? 'warning' : 'primary'}
              leftIcon={category.priority === 1 ? 'far fa-crown' : undefined}
            >
              {`Priority #${category.priority}`}
            </Badge>
          )}
        </Box>

        {category.priority === 1 && category.tip && <Text>{category.tip}</Text>}
        {isTopCategory && isFinancialPlanEnabled ? (
          <>
            {category.goals.length > 0 ? (
              <CategoryGoals
                isTopCategory
                clientId={clientId}
                goals={category.goals}
                isManagingGoals={isManagingGoals}
                canAddGoals={canAddGoals}
                onManageGoals={() => setIsManagingGoals(state => !state)}
                onAddGoal={() => onSetGoal(category.id)}
                categoryId={category.id}
              />
            ) : (
              <Button
                variant={category.priority === 1 ? 'primary' : 'secondary'}
                onClick={() => onSetGoal(category.id)}
                disabled={!canAddGoals}
              >
                Set Goal
              </Button>
            )}
          </>
        ) : (
          <>
            <Box display="flex" flexDirection="column">
              <Text fontSize="small" fontWeight={500} height={24} css="align-self: flex-end">
                {getFulfillmentLabel(category.fulfillment)}
              </Text>

              <Slider
                step={1}
                min={0}
                max={10}
                ariaLabels={[`${category.tip} Thumb`]}
                values={[category.fulfillment]}
                onChange={() => {
                  // noop
                }}
                showTooltip={false}
                disabled
                mb="16"
              />
            </Box>

            {category.goals.length > 0 && (
              <CategoryGoals
                clientId={clientId}
                goals={category.goals}
                canAddGoals={canAddGoals}
                isManagingGoals={isManagingGoals}
                onManageGoals={() => setIsManagingGoals(state => !state)}
                categoryId={category.id}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
